import { FC, ReactElement, useEffect, useState, memo } from 'react'
import { Link } from 'react-router-dom'
import { useSwipeable } from 'react-swipeable'

import { fetchBlogs } from '../../../features/blogs/blogSlice'
import { useI18n } from '../../../hooks'
import { useAppDispatch, useAppSelector } from '../../../store'
import { Blog } from '../../../types'
import { getDefaultBlogBannerImage } from '../../../utils/blog'
import { LeftArrow, RightArrow } from '../../atoms/carouselComponent/Arrows'
import ExploreCard from '../components/exploreCard'
import './index.css'

type Slide = {
  element: ReactElement | Element
  class: string
}

const ExploreCarousel: FC = (): ReactElement => {
  const dispatch = useAppDispatch()
  const { blogs, isLoading } = useAppSelector((state) => state.blogReducer)

  const [slideCurrent, setSlideCurrent] = useState(0)
  const [slides, setSlides] = useState<Slide[]>([])
  const [blogsData, setBlogsData] = useState<Blog[] | undefined>()

  const { t } = useI18n()

  useEffect(() => {
    if (isLoading) return
    if (!blogs.length) Promise.all([dispatch(fetchBlogs())])
    setBlogsData([...blogs])
  }, [dispatch, blogs, isLoading])

  useEffect(() => {
    if (blogsData) {
      const newSlides: Slide[] = blogs.slice(0, 5).map((blog, index) => {
        const { title, slug } = blog

        return {
          element: <ExploreCard title={title} link={'/blog/' + slug} backgroundImage={getDefaultBlogBannerImage(blog)} />,
          class: 'slider-single proactivede',
        }
      })
      if (newSlides.length > 0) {
        newSlides[0].class = 'slider-single active'
        newSlides[newSlides.length - 1].class = 'slider-single preactive'
        newSlides[1].class = 'slider-single proactive'
        setSlides(newSlides)
      }
    }
  }, [blogs, blogsData])

  const slideRight = () => {
    let preactiveSlide
    let proactiveSlide
    let slideCurrentLoc = slideCurrent

    const activeClass = 'slider-single active'
    const slideTotal = slides.length - 1
    const slide = [...slides]

    if (slideCurrentLoc < slideTotal) {
      slideCurrentLoc++
    } else {
      slideCurrentLoc = 0
    }
    if (slideCurrentLoc > 0) {
      preactiveSlide = slide[slideCurrentLoc - 1]
    } else {
      preactiveSlide = slide[slideTotal]
    }
    const activeSlide = slide[slideCurrentLoc]

    if (slideCurrentLoc < slideTotal) {
      proactiveSlide = slide[slideCurrentLoc + 1]
    } else {
      proactiveSlide = slide[0]
    }

    slide.forEach((slid, index) => {
      if (slid.class.includes('preactivede')) {
        slid.class = 'slider-single proactivede'
      }
      if (slid.class.includes('preactive')) {
        slid.class = 'slider-single preactivede'
      }
    })

    preactiveSlide.class = 'slider-single preactive'
    activeSlide.class = activeClass
    proactiveSlide.class = 'slider-single proactive'
    setSlides(slide)
    setSlideCurrent(slideCurrentLoc)
  }

  const slideLeft = () => {
    let preactiveSlide
    let proactiveSlide
    let slideCurrentLoc = slideCurrent
    const slideTotal = slides.length - 1
    const slide = [...slides]

    if (slideCurrentLoc > 0) {
      slideCurrentLoc--
    } else {
      slideCurrentLoc = slideTotal
    }

    if (slideCurrentLoc < slideTotal) {
      proactiveSlide = slide[slideCurrentLoc + 1]
    } else {
      proactiveSlide = slide[0]
    }
    const activeSlide = slide[slideCurrentLoc]

    if (slideCurrentLoc > 0) {
      preactiveSlide = slide[slideCurrentLoc - 1]
    } else {
      preactiveSlide = slide[slideTotal]
    }

    slide.forEach((slid, index) => {
      if (slid.class.includes('proactivede')) {
        slid.class = 'slider-single preactivede'
      }
      if (slid.class.includes('proactive')) {
        slid.class = 'slider-single proactivede'
      }
    })
    preactiveSlide.class = 'slider-single preactive'
    activeSlide.class = 'slider-single active'
    proactiveSlide.class = 'slider-single proactive'
    setSlides(slide)

    setSlideCurrent(slideCurrentLoc)
  }

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => slideRight(),
    onSwipedRight: () => slideLeft(),
  })
  return slides.length ? (
    <>
      <div className="section-title section-title-with-view-more">
        <h1>{t('Common_explore')}</h1>
        <Link to="/blogs" className="light">
          {t('Common_viewAll')}
        </Link>
      </div>
      <div className="explore-carousel" {...swipeHandlers} style={{ height: 400, touchAction: 'pan-y' }}>
        <div className="explore-carousel-slider-container">
          <div className="explore-carousel-slider-arrows">
            <LeftArrow previous={slideLeft} />
            <RightArrow next={slideRight} />
          </div>

          <div className="explore-carousel-slider-content">
            {slides.map((blog, index) => (
              <div key={index} className={blog.class}>
                <div className="explore-carousel-slider-single-content">{blog.element}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  ) : (
    <></>
  )
}

export default memo(ExploreCarousel)
